import { useCallback, useEffect, useState } from 'react';
import { Segment } from "peaks.js";
import useMediaState from "@/common/hooks/useMediaState";
import { MediaState } from "@/types/types";
import useAudioProgress from "@/common/hooks/useAudioProgress";
import useAudioAdapter from "@/common/hooks/useAudioAdapter.ts";
import useAudioEditorPeaksInstance from './useAudioEditorPeaksInstance';

const useSegmentProgress = (segment: Segment) => {
  const [audioAdapter] = useAudioAdapter();
  const [peaksInstance] = useAudioEditorPeaksInstance();
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [mediaState] = useMediaState();
  const isPlaying = MediaState.PLAYING === mediaState
  const {
    progress: audioProgress,
  } = useAudioProgress()

  const getSegmentDuration = useCallback(() => {
    return segment.endTime - segment.startTime
  }, [
    segment.endTime,
    segment.startTime
  ])

  const getProgress = useCallback(() => {
    if (!audioAdapter) {
      return
    }
    return ((audioAdapter?.getCurrentTime() - segment.startTime) / getSegmentDuration()) * 100 || 0
  }, [
    audioAdapter,
    getSegmentDuration,
    segment.startTime
  ])

  const getSegmentTimeLeft = useCallback(() => {
    if (!audioAdapter) {
      return
    }
    return getSegmentDuration() - (audioAdapter?.getCurrentTime() - segment.startTime)
  }, [
    audioAdapter,
    getSegmentDuration,
    segment.startTime
  ])

  const getSegmentCurrentTime = useCallback(() => {
    if (!audioAdapter) {
      return
    }
    return audioAdapter?.getCurrentTime() - segment.startTime
  }, [
    audioAdapter,
    segment.startTime
  ])

  const update = useCallback(() => {
    const timeLeft = getSegmentTimeLeft()
    const currentTime = getSegmentCurrentTime()
    const progress = getProgress()
    if (!audioAdapter || !progress || !currentTime || !timeLeft) {
      return
    }

    setProgress(progress);
    setCurrentTime(currentTime)
    setTimeLeft(timeLeft)
  }, [
    audioAdapter,
    getProgress,
    getSegmentCurrentTime,
    getSegmentTimeLeft
  ])

  const handleSeeked = useCallback(() => {
    setProgress(getProgress() || 0)
    setCurrentTime(getSegmentCurrentTime() || 0)
    setTimeLeft(getSegmentTimeLeft() || 0)
  }, [
    getProgress,
    getSegmentCurrentTime,
    getSegmentTimeLeft
  ])

  useEffect(() => {
    if (!audioAdapter || !isPlaying) {
      return
    }

    update()
  }, [
    audioAdapter,
    audioProgress,
    isPlaying,
    update
  ])

  useEffect(() => {
    if (!peaksInstance) {
      return
    }

    peaksInstance?.on("player.seeked", handleSeeked)

    return () => {
      peaksInstance?.off("player.seeked", handleSeeked)
    };
  }, [
    peaksInstance,
    handleSeeked,
    segment
  ]);

  if (!isPlaying) {
    return {
      progress: getProgress(),
      currentTime: getSegmentCurrentTime(),
      timeLeft: getSegmentTimeLeft(),
    }
  }

  return { progress, currentTime, timeLeft };
}

export default useSegmentProgress;
